import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "../site/Reveal";

const Frame = ({ src, alt, className, style }) => (
  <motion.figure style={style} className={`absolute overflow-hidden bg-slate-200 shadow-[0_30px_60px_-30px_rgba(14,28,58,0.45)] ${className}`}>
    <img src={src} alt={alt} className="h-full w-full object-cover" />
  </motion.figure>
);

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const f = typeof window !== "undefined" && window.innerWidth < 1024 ? 0.2 : 1;
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120 * f]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -40 * f]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200 * f]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} className="relative overflow-hidden bg-white pt-[104px] md:pt-[130px] pb-16 md:pb-24" data-testid="home-hero">
      <div className="absolute inset-0 grid-lines pointer-events-none" aria-hidden="true" />
      <div className="container-x relative grid gap-12 lg:grid-cols-12 lg:gap-8 lg:items-center">
        <div className="lg:col-span-7">
          <Reveal y={10}>
            <p className="inline-flex items-center gap-3 eyebrow">
              <span className="h-px w-10 bg-[color:var(--teal)]" /> Internationale Direktvermittlung · Lübeck
            </p>
          </Reveal>
          <h1 className="mt-7 text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5.2rem] font-extrabold tracking-[-0.035em] text-[color:var(--navy)]" data-testid="hero-heading">
            <MaskLines
              lines={[
                "Internationale Fachkräfte.",
                "Passende Arbeitgeber.",
                <span key="p" className="font-serif-accent font-normal text-[color:var(--teal-ink)]">Neue Perspektiven.</span>,
              ]}
              delay={0.15} step={0.14}
            />
          </h1>
          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-slate-600" data-testid="hero-subtext">
              PACTORA Personalpartner verbindet Unternehmen in ganz Deutschland mit Arbeitskräften aus dem Ausland. Wir begleiten die Direktvermittlung – vom Anforderungsprofil und der Bewerberauswahl bis zur Abstimmung des Arbeitsbeginns.
            </p>
          </Reveal>
          <Reveal delay={0.75} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/personal-anfragen" data-testid="hero-cta-inquiry" className="group inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--navy)] px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:bg-[color:var(--navy-deep)] active:scale-[0.97]">
              Personal anfragen <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/bewerber-registrierung" data-testid="hero-cta-register" className="group inline-flex items-center justify-center gap-3 rounded-full border-2 border-[color:var(--teal)] px-7 py-[14px] font-semibold text-[color:var(--navy)] transition-[background-color] hover:bg-[color:var(--teal)] hover:text-white">
              Als Bewerber registrieren <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="lg:col-span-5" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative mx-auto aspect-[1/1] sm:aspect-[4/5] w-full max-w-[520px]"
          >
            <Frame src="/img/truck.webp" alt="Symbolbild: Berufskraftfahrer vor einem Lkw" style={{ y: y2 }} className="left-[6%] top-0 h-[78%] w-[62%] rounded-t-[999px] rounded-b-[2rem]" />
            <Frame src="/img/nurse2.webp" alt="Symbolbild: Pflegekraft im Krankenhaus" style={{ y: y1 }} className="right-0 top-[30%] h-[46%] w-[46%] rounded-[2rem] rounded-tl-[6rem] border-[6px] border-white" />
            <Frame src="/img/craft.webp" alt="Symbolbild: Elektriker bei der Arbeit" style={{ y: y3 }} className="bottom-[2%] left-0 h-[30%] w-[44%] rounded-[1.5rem] border-[6px] border-white" />
            <motion.div style={{ y: y1 }} className="absolute right-[4%] top-[6%] h-24 w-24 md:h-28 md:w-28 rounded-full bg-[color:var(--teal)] grid place-items-center text-center text-white">
              <span className="text-[10px] font-bold uppercase leading-tight tracking-[0.14em]">Ganz<br />Deutsch&shy;land</span>
            </motion.div>
            <p className="absolute -bottom-7 right-0 text-[11px] text-slate-500">Symbolbilder</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
